if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.swiperOptions = swiperOptions; // [into - swiper slider fix] store parsed options for later use in initSlider()

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');

      this.sliderOptions = {
        a11y: false,

        // [into - swiper slider fix] slightly higher threshold reduces accidental micro-swipes on mobile
        threshold: 5,

        spaceBetween: swiperOptions.spaceBetweenMobile || 12,

        // [into - swiper slider fix] keeps the existing "peek next slide" design from Liquid, usually 1.3 on mobile
        slidesPerView: swiperOptions.slidesPerViewMobile || 1.3,

        // [into - swiper slider fix] avoids fractional slide widths/transforms like 301.077px, which can cause iOS snap-back
        roundLengths: true,

        // [into - swiper slider fix] removes elastic overdrag at the beginning/end that can look like a slide "cofka"
        resistanceRatio: 0,

        // [into - swiper slider fix] prevents unnecessary slider behavior when there are too few slides
        watchOverflow: true,

        // [into - swiper slider fix] keeps swipe behavior predictable: one logical slide per snap
        slidesPerGroup: 1,

        // [into - swiper slider fix] keeps active index normalized after touchEnd/update
        normalizeSlideIndex: true,

        // [into - swiper slider fix] makes mobile swipe snapping a bit more stable without changing the visual design
        longSwipesRatio: 0.25,
        longSwipesMs: 300,

        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 12
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 12
          }
        }
      };

      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }

      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay,

          // [into - swiper slider fix] prevents autoplay from fighting the user's manual swipe after interaction
          disableOnInteraction: true,

          // [into - swiper slider fix] native Swiper option; safer than only manually stopping/starting on hover
          pauseOnMouseEnter: true
        };
      }

      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }

      this.initSlider();
      this.syncHeights();

      // [into - swiper slider fix] keep a stable reference so it can be removed if the element is disconnected
      this._onResize = () => {
        this.syncHeights();

        if (this.slider) {
          this.slider.update();
        }
      };

      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      // [into - swiper slider fix] cleanup to avoid duplicated listeners when Shopify sections reload
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }

      if (this.slider && !this.slider.destroyed) {
        this.slider.destroy(true, true);
      }
    }

    syncHeights() {
      const textSlide = this.querySelector('.products-feed__card-text-custom-slide');
      const actionElement = this.querySelector('.product-card__actions');

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;
        textSlide.style.height = `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');

      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;

            this.parent.classList.remove('hidden');
            this.initSlider();
            this.syncHeights();

            // [into - swiper slider fix] recommendations are fetched dynamically; update after DOM paint
            requestAnimationFrame(() => {
              if (!this.slider || this.slider.destroyed) return;

              this.slider.update();
              this.slider.slideTo(0, 0);
            });

            // [into - swiper slider fix] second update catches image/layout changes after product cards render
            setTimeout(() => {
              if (!this.slider || this.slider.destroyed) return;

              this.slider.update();
              this.syncHeights();
            }, 300);
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    initSlider(slider = this) {
      // [into - swiper slider fix] avoid double initialization if Shopify reloads the section
      if (this.slider && !this.slider.destroyed) {
        this.slider.destroy(true, true);
      }

      this.slider = new Swiper(slider, this.sliderOptions);

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;

      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });

      if (this.swiperOptions?.navigation && this.slider.navigation) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach((button, i) => {
          if (!button) return;

          let type = 'Previous';

          if (i === 1) {
            type = 'Next';
          }

          button.setAttribute('aria-label', `${type} slide`);
          button.setAttribute(
            'aria-controls',
            this.slider.wrapperEl.id
          );
        });
      }

      this.slider.wrapperEl.addEventListener('focusin', e => {
        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');

        // [into - swiper slider fix] guard against focus events outside slides
        if (!slide) return;

        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide =
          slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);

          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;

        // prettier-ignore
        const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);

        const isSlideVisible =
          slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) {
          return;
        }

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });

      // [into - swiper slider fix] only attach autoplay listeners when autoplay actually exists
      if (this.slider.autoplay) {
        this.slider.wrapperEl.addEventListener('mouseenter', () => {
          this.slider.autoplay.stop();
        });

        this.slider.wrapperEl.addEventListener('mouseleave', () => {
          if (
            !this.classList.contains('product--open-on-quick-cart') &&
            this.sliderOptions.autoplay
          ) {
            this.slider.autoplay.start();
          }
        });
      }

      // [into - swiper slider fix] update after touch end to stabilize Swiper's snap point on iOS/Chrome
      this.slider.on('touchEnd', () => {
        requestAnimationFrame(() => {
          if (!this.slider || this.slider.destroyed) return;

          this.slider.update();
        });
      });

      // [into - swiper slider fix] update after transition end, useful when slide widths are recalculated
      this.slider.on('transitionEnd', () => {
        if (!this.slider || this.slider.destroyed) return;

        this.slider.update();
      });

      if (
        this.hasAttribute('data-init-quick-cart') &&
        document.querySelector('quick-cart-drawer') &&
        this.querySelector('.swiper-slide')
      ) {
        document.querySelector('quick-cart-drawer').init();
      }

      this.querySelectorAll('product-card').forEach(card => {
        card.init();
      });

      if (Shopify.designMode) {
        if (
          this.hasAttribute('data-init-quick-cart') &&
          document.querySelector('quick-cart-drawer') &&
          this.querySelector('.swiper-slide')
        ) {
          document.querySelector('quick-cart-drawer').init();
        }
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}